import React, { useState } from "react";
import { X, Send, Loader2, Mail } from "lucide-react";
import toast from "react-hot-toast";
import { sendOfferToBuyer } from "../../../api/propertyManagement";

const SendToBuyerModal = ({ isOpen, onClose, offer, onSent }) => {
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen || !offer) return null;

  const buyerName = `${offer.first_name || offer.buyer_name || ""} ${offer.last_name || ""}`.trim();

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const res = await sendOfferToBuyer(offer.id, note);
      const data = res?.data || {};
      toast.success("Sent to buyer");
      if (data.email_sent === false) {
        toast.error("Buyer email failed to send (will retry)", { duration: 4500 });
      }
      onSent?.(data);
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail || "Failed to send to buyer";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
        <div className="px-6 py-5 bg-gradient-to-r from-gray-50 to-white border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-emerald-100">
              <Send className="w-5 h-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-900">Send Update to Buyer</h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Status: {offer.status_display || offer.status}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="rounded-xl border border-emerald-100 bg-emerald-50/40 p-4 flex items-start gap-3">
            <Mail className="w-5 h-5 text-emerald-700 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-semibold">{buyerName || "Buyer"}</p>
              <p className="text-xs text-gray-500 mt-0.5">
                {offer.buyer_email || offer.email}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-gray-500 uppercase tracking-wider">
              Note to buyer (optional)
            </label>
            <textarea
              rows={5}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Add a personal note before forwarding the vendor response..."
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-900 bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200/50 focus:border-emerald-400 transition-all resize-none placeholder:text-gray-400"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-all disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-700 transition-all disabled:opacity-50 shadow-lg shadow-emerald-600/25 flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" /> Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" /> Send to Buyer
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendToBuyerModal;
